import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Flower2, ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API } from '@/App';

function AuthPage({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuthAndRedirect = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          await axios.get(`${API}/user/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          navigate('/dashboard');
          return;
        } catch (error) {
          if (error.response?.status === 401) {
            localStorage.clear();
          }
        }
      }
      setCheckingAuth(false);
    };
    
    checkAuthAndRedirect();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('display_name', response.data.display_name);
      localStorage.setItem('user_id', response.data.user_id);

      toast.success(`Welcome Back, ${response.data.display_name}!`);
      setIsAuthenticated(true);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Sparkles className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-6 py-12">
      <Button
        data-testid="back-to-home-button"
        onClick={() => navigate('/')}
        variant="ghost"
        className="absolute top-6 left-6 text-secondary-900"
      >
        <ArrowLeft className="mr-2 w-5 h-5" /> Back to Home
      </Button>

      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl p-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-4">
              <Flower2 className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-4xl font-serif font-bold text-secondary-900 mb-2">
              Welcome Back
            </h2>
            <p className="text-secondary-600">
              Sign in to continue your transformation
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
            <div>
              <Label htmlFor="email" className="text-secondary-900 font-medium mb-2 block">
                Email Address
              </Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-4 py-6 rounded-xl border-2 border-primary/20 focus:border-primary transition-colors"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-secondary-900 font-medium mb-2 block">
                Password
              </Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="w-full px-4 py-6 rounded-xl border-2 border-primary/20 focus:border-primary transition-colors"
              />
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-secondary-600 mb-4">Don't have an account?</p>
            <Button
              data-testid="go-to-pricing-button"
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary/5 px-6 py-4 rounded-full"
            >
              Purchase a Plan to Get Started
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
